import { Box } from "@mui/material";
import { OLPaper } from "OLPaper";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useWallets } from "api/wallets";
import { useAppSelector } from "app/hooks";
import { FormButtons } from "components/FormControls/FormButtons";
import { Toggle } from "components/FormControls/Toggle";
import {
  prepareBECampaignLimitFormatForFE,
  sortCampaignTranslations,
} from "components/Layout/Campaign/Common/campaignHelpers";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { surveyQuery } from "constants/general";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik, FormikErrors } from "formik";
import { useMessage } from "hooks/intl";
import { useNavigate, useParams } from "react-router-dom";
import { selectDefaultLanguageData } from "reduxState/translations/translationsSlice";
import { BEWalletTypeFormat, FEWalletTypeFormat } from "types/wallets";
import {
  backendErrors,
  parseCampaignTranslationsArrayError,
} from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import { BasicSettings } from "./BasicSettings";
import { UnitSettings } from "./UnitSettings";
import { useStyles } from "./WalletType.styles";
import { formatWalletValues } from "./utils";

export function WalletTypeForm(props: {
  editMode?: boolean;
  initialValues?: BEWalletTypeFormat;
  initialErrors?: FormikErrors<FEWalletTypeFormat>;
  onSaveCallback?: (wallet: BEWalletTypeFormat) => void;
  onCancelCallback?: () => void;
}) {
  const classes = useStyles();
  const { id: walletTypeCode } = useParams<{ id: string }>();
  const { notify } = useNotifications();
  const { addWalletType, editWalletType } = useWallets();
  const navigate = useNavigate();
  const { message } = useMessage();
  const defaultLang = useAppSelector(selectDefaultLanguageData);
  const isDefaultWallet = props.initialValues?.isDefault;
  const isNegativeBalanceAlreadySet =
    props.initialValues && props.initialValues.allowNegativeBalance;

  const initialValues: FEWalletTypeFormat =
    (props.editMode && props.initialValues) || props.initialValues
      ? {
          active: props.initialValues.active,
          allowNegativeBalance: props.initialValues.allowNegativeBalance,
          code: props.initialValues.code,
          isDefault: props.initialValues.isDefault,
          limits: prepareBECampaignLimitFormatForFE(props.initialValues.limits),
          translations:
            props.initialValues.translations &&
            sortCampaignTranslations(
              defaultLang?.code,
              Object.keys(props.initialValues.translations).map((key) => ({
                description:
                  props.initialValues?.translations[key]?.description,
                locale: key,
                name: props.initialValues?.translations[key]?.name,
              })),
            ),
          allTimeNotLocked: props.initialValues.allTimeNotLocked
            ? "true"
            : "false",
          unitDaysActiveCount: props.initialValues.unitDaysActiveCount,
          unitDaysExpiryAfter: props.initialValues.unitDaysExpiryAfter,
          unitDaysLocked: props.initialValues.unitDaysLocked,
          unitExpiryDate: props.initialValues.unitExpiryDate,
          unitPluralName: props.initialValues.unitPluralName,
          unitSingularName: props.initialValues.unitSingularName,
          unitYearsActiveCount: props.initialValues.unitYearsActiveCount,
          walletTypeId: props.initialValues.walletTypeId,
        }
      : {
          active: false,
          allTimeNotLocked: "true",
          limits: {
            points: { interval: { type: "noLimit" } },
            pointsPerMember: { interval: { type: "noLimit" } },
          },
          translations: [
            defaultLang && {
              description: "",
              locale: defaultLang.code,
              name: "",
            },
          ],
          unitDaysExpiryAfter: "all_time_active",
          allowNegativeBalance: false,
        };

  return (
    <>
      <Formik
        enableReinitialize
        initialStatus={{}}
        initialValues={initialValues}
        initialErrors={props.initialErrors}
        onSubmit={async (values, { setSubmitting, setStatus }) => {
          setSubmitting(true);
          setStatus({});

          try {
            const properties: BEWalletTypeFormat = formatWalletValues(
              values,
              props.editMode,
            );

            const response =
              props.editMode && props.initialValues
                ? await editWalletType(walletTypeCode as string, properties)
                : await addWalletType(properties);

            props.onSaveCallback
              ? props.onSaveCallback({
                  walletTypeId: (response as { data: { walletTypeId: string } })
                    .data.walletTypeId,
                  ...properties,
                })
              : navigate(
                  props.editMode
                    ? links.walletTypesList
                    : `${links.walletTypesList}${surveyQuery}`,
                );

            notify(
              message({
                id: props.editMode ? "walletTypeUpdated" : "walletTypeAdded",
              }),
            );
          } catch (exception) {
            setSubmitting(false);
            notify(getErrorMessage(exception));
            const errors = backendErrors(getBackendErrors(exception));
            const parsedErrors = {
              ...errors,
              translations: parseCampaignTranslationsArrayError(
                errors?.translations,
                values?.translations,
              ),
            };
            setStatus(parsedErrors);
            focusError();
            return;
          }
        }}
      >
        {({
          handleSubmit,
          isSubmitting,
          status,
          values,
          dirty,
          initialValues,
        }) => {
          const isDefaultWalletActiveError = !!(
            status?.["active"] && isDefaultWallet
          );

          return (
            <Form onSubmit={handleSubmit}>
              {status?.[""] && (
                <span className={`${classes.error} error-anchor`}>
                  {status[""]}
                </span>
              )}
              <div className={classes.sectionHeader}>
                {message({ id: "basicSettings" })}
              </div>
              <BasicSettings editMode={props.editMode} />

              <div className={classes.sectionHeader}>
                {message({ id: "unitSettings" })}
              </div>
              <UnitSettings
                isNegativeBalanceAlreadySet={isNegativeBalanceAlreadySet}
              />

              {!isDefaultWallet && (
                <>
                  <div className={classes.sectionHeader}>
                    {message({ id: "walletTypeStatus" })}
                  </div>
                  <OLPaper className={classes.OLPaper}>
                    {!values.isDefault && (
                      <Toggle
                        className={classes.SelectInput}
                        data-test-id="activate-wallet"
                        label={message({ id: "active" })}
                        name="active"
                      />
                    )}
                    <div className={classes.fieldDescription}>
                      {message({ id: "activateWalletTypeInfo" })}
                    </div>
                  </OLPaper>
                </>
              )}
              {!isSubmitting && (
                <LeaveSiteGuard
                  warnBeforeRefresh
                  isDirty={dirty}
                  initialValues={initialValues}
                  currentValues={values}
                />
              )}
              {isDefaultWalletActiveError && (
                <span className={`${classes.error} error-anchor`}>
                  {status["active"]}
                </span>
              )}
              <Box mt={12} />
              <FormButtons
                disableSubmit={isSubmitting}
                onCancelClick={() => {
                  if (props.onCancelCallback) {
                    return props.onCancelCallback();
                  }
                  navigate(links.walletTypesList);
                }}
                onSubmitClick={() => handleSubmit()}
                {...(!props.editMode && { submitMessage: "addWalletType" })}
              />
            </Form>
          );
        }}
      </Formik>
    </>
  );
}
